import { Component, useState, type ErrorInfo, type ReactNode } from "react";

import { useTranslation } from "react-i18next";

import { AlertTriangle, RefreshCw, Bug } from "lucide-react";

import * as Sentry from "@sentry/electron/renderer";

import { GridPattern } from "./ui/grid-pattern";

import ReportProblemModal from "./ReportProblemModal/ReportProblemModal";

interface Props {
  children: ReactNode;

  fallback?: ReactNode;
}

interface State {
  hasError: boolean;

  error?: Error;
}

function ErrorBoundaryFallback({
  error,
  onRetry,
}: {
  error?: Error;
  onRetry: () => void;
}) {
  const { t } = useTranslation("common");

  const [reportOpen, setReportOpen] = useState(false);

  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-auto bg-background p-10">
      <GridPattern
        width={40}
        height={40}
        className="[mask-image:radial-gradient(600px_circle_at_center,white,transparent)] stroke-primary/15"
      />

      <div className="relative z-10 flex w-full max-w-lg flex-col items-center gap-6 text-center">
        {/* Icon */}
        <div className="flex size-20 items-center justify-center rounded-2xl border border-destructive/20 bg-destructive/10">
          <AlertTriangle
            className="size-10 text-destructive"
            strokeWidth={1.5}
          />
        </div>

        {/* Text */}
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold text-foreground">
            {t("ERROR_BOUNDARY.title")}
          </h1>
          <p className="text-base text-muted-foreground">
            {t("ERROR_BOUNDARY.subtitle")}
          </p>
          <p className="text-sm text-muted-foreground/70">
            {t("ERROR_BOUNDARY.message")}
          </p>
        </div>

        {/* Technical details */}
        {error && (
          <details className="w-full cursor-pointer rounded-xl border border-border/40 bg-secondary/30 p-4 text-left">
            <summary className="text-sm font-semibold text-muted-foreground">
              {t("ERROR_BOUNDARY.details")}
            </summary>
            <pre className="mt-3 whitespace-pre-wrap break-words text-xs leading-relaxed text-muted-foreground/70">
              {error.message}
              {"\n\n"}
              {error.stack}
            </pre>
          </details>
        )}

        {/* Actions */}
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center gap-2 rounded-xl border border-accent/50 bg-accent/20 px-6 py-3 text-sm font-semibold text-foreground hover:bg-accent/30 hover:border-accent/70 hover:-translate-y-0.5"
          >
            <RefreshCw className="size-4" />
            {t("ERROR_BOUNDARY.retry")}
          </button>

          <button
            type="button"
            onClick={() => setReportOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-border/50 bg-secondary/40 px-6 py-3 text-sm font-semibold text-foreground hover:bg-secondary/60 hover:-translate-y-0.5"
          >
            <Bug className="size-4" />
            {t("ERROR_BOUNDARY.report", "Reportar este error")}
          </button>
        </div>
      </div>

      <ReportProblemModal
        isOpen={reportOpen}
        onClose={() => setReportOpen(false)}
        capturedError={error}
        source="error-boundary"
      />
    </div>
  );
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,

      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("Error capturado por ErrorBoundary:", error, errorInfo);

    // Report the render crash to Sentry / GlitchTip (no-op when the SDK is

    // disabled — no VITE_SENTRY_DSN; see main.tsx). The React component stack

    // rides along as extra context.

    Sentry.captureException(error, {
      tags: { source: "react-render" },

      extra: { componentStack: errorInfo.componentStack },
    });
  }

  componentDidMount(): void {
    window.addEventListener("unhandledrejection", this.handlePromiseRejection);
  }

  componentWillUnmount(): void {
    window.removeEventListener(
      "unhandledrejection",
      this.handlePromiseRejection,
    );
  }

  handlePromiseRejection = (event: PromiseRejectionEvent): void => {
    // Ignore non-critical media element errors (e.g. video play failures)

    const reason = event.reason;

    if (reason instanceof DOMException) {
      const ignorable = ["NotSupportedError", "AbortError", "NotAllowedError"];

      if (ignorable.includes(reason.name)) {
        console.warn(
          "Non-critical media error ignored by ErrorBoundary:",

          reason.name,

          reason.message,
        );

        event.preventDefault();

        return;
      }
    }

    console.error("Promise rejection capturada por ErrorBoundary:", reason);

    const error = reason instanceof Error ? reason : new Error(String(reason));

    // Sentry's global handlers auto-capture unhandled rejections, so we don't

    // report here — we only surface the fallback UI.

    this.setState({
      hasError: true,

      error,
    });

    event.preventDefault();
  };

  handleRetry = () => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorBoundaryFallback
          error={this.state.error}
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
